import { orderedPlaybackClientLogBody } from "@/api/playbackEventOrdering";
import type { PlaybackClientLogEntry, PlaybackClientLogRequest } from "@/types/api";

// The public relay rejects JSON request bodies near 64 KiB before they reach
// KnotFin, despite the inner ModSecurity limit being 128 KiB. Keep a full
// quarter of headroom for envelope/shaping changes and proxy differences.
export const PLAYBACK_CLIENT_LOG_REQUEST_TARGET_BYTES = 48 * 1024;
export const PLAYBACK_CLIENT_LOG_EDGE_LIMIT_BYTES = 64 * 1024;
export const PLAYBACK_CLIENT_LOG_MAX_BATCH_ENTRIES = 200;

const utf8Encoder = new TextEncoder();

export function playbackClientLogRequestByteLength(body: PlaybackClientLogRequest): number {
  const orderedBody = orderedPlaybackClientLogBody(body);
  return utf8Encoder.encode(JSON.stringify(orderedBody)).byteLength;
}

/**
 * A client-log request that cannot be made smaller and therefore can never
 * succeed. Typed so the flush path classifies it as terminal: treating it as a
 * generic error makes it retryable, which requeues the batch forever and stops
 * every later diagnostic from uploading (#818).
 */
export class PlaybackClientLogTooLargeError extends Error {
  constructor(
    public byteLength: number,
    public maxBytes: number,
  ) {
    super(`playback client-log request is ${byteLength} bytes; target is ${maxBytes}`);
    this.name = "PlaybackClientLogTooLargeError";
  }
}

export interface PlaybackClientLogBatch {
  entries: PlaybackClientLogEntry[];
  serializedBytes: number;
  /**
   * True when the batch is a single entry that cannot fit on its own. The
   * caller must drop it rather than retry: no smaller batch exists, so
   * requeueing it wedges the queue head forever.
   */
  oversizedEntry: boolean;
}

interface PlaybackClientLogBatchOptions {
  maxBytes?: number;
  maxEntries?: number;
  fromEnd?: boolean;
}

/**
 * Choose the largest prefix (or suffix, with `fromEnd`) of `entries` whose
 * serialized request body stays within `maxBytes`.
 *
 * Each entry is measured once and the running total is accumulated, rather than
 * re-serializing the whole batch per candidate. That difference matters: this
 * runs on the playback path, and re-serializing a growing 200-entry batch costs
 * megabytes of string work per flush on exactly the low-power devices that
 * stream (#818).
 *
 * The size of a batch is not quite the sum of its entries — the wrapper and the
 * separating commas add bytes — so both are accounted for explicitly, and the
 * result is verified against a real serialization before being returned.
 */
export function selectPlaybackClientLogBatch(
  entries: readonly PlaybackClientLogEntry[],
  options: PlaybackClientLogBatchOptions = {},
): PlaybackClientLogBatch {
  const maxBytes = options.maxBytes ?? PLAYBACK_CLIENT_LOG_REQUEST_TARGET_BYTES;
  const maxEntries = Math.max(1, options.maxEntries ?? PLAYBACK_CLIENT_LOG_MAX_BATCH_ENTRIES);
  const fromEnd = options.fromEnd === true;
  const available = fromEnd
    ? entries.slice(Math.max(0, entries.length - maxEntries))
    : entries.slice(0, maxEntries);

  if (available.length === 0) {
    return { entries: [], serializedBytes: playbackClientLogRequestByteLength({ entries: [] }), oversizedEntry: false };
  }

  const envelopeBytes = playbackClientLogRequestByteLength({ entries: [] });
  const candidates = fromEnd ? [...available].reverse() : available;

  const selected: PlaybackClientLogEntry[] = [];
  let total = envelopeBytes;

  for (const entry of candidates) {
    // orderedPlaybackClientLogBody stamps a source id onto each entry, so the
    // entry must be measured through the same shaping the request will apply.
    const [shaped] = orderedPlaybackClientLogBody({ entries: [entry] }).entries;
    const entryBytes = utf8Encoder.encode(JSON.stringify(shaped)).byteLength;
    const separatorBytes = selected.length === 0 ? 0 : 1;
    const projected = total + entryBytes + separatorBytes;

    if (projected > maxBytes && selected.length > 0) {
      break;
    }

    selected.push(entry);
    total = projected;

    if (projected > maxBytes) {
      // A single entry that cannot fit. Return it flagged so the caller drops
      // it deliberately instead of retrying something that can never succeed.
      // Exactly one entry by construction, so order does not depend on `fromEnd`.
      return { entries: selected, serializedBytes: projected, oversizedEntry: true };
    }
  }

  const ordered = fromEnd ? selected.reverse() : selected;
  // The accumulated total is a projection; confirm it against the real body so
  // a shaping change can never silently push a batch over the edge limit.
  const serializedBytes = playbackClientLogRequestByteLength({ entries: ordered });

  return { entries: ordered, serializedBytes, oversizedEntry: false };
}
